/**
 * Pull a human-readable message out of an error response body.
 *
 * Our FastAPI services answer with `{ detail }`, not `{ message }`, so reading
 * only `message` meant no server message ever reached the UI. `detail` can also
 * arrive as a JSON string (one service proxying another's body) or as FastAPI's
 * validation array, and a proxy 413/502 may not be JSON at all.
 */
function fromDetail(detail) {
  if (typeof detail === 'string') return unwrapJsonString(detail)
  if (Array.isArray(detail)) {
    const first = detail.find((item) => typeof item?.msg === 'string')
    return first ? String(first.msg) : null
  }
  if (detail && typeof detail === 'object' && typeof detail.detail === 'string') {
    return detail.detail
  }
  return null
}

/** A `detail` that is itself a serialized `{"detail": "..."}` body. */
function unwrapJsonString(value) {
  const text = value.trim()
  if (!text.startsWith('{') && !text.startsWith('[')) return text || null
  try {
    return fromDetail(JSON.parse(text)) ?? text
  } catch {
    return text
  }
}

export function serverErrorMessage(data) {
  if (typeof data === 'string') {
    // Non-JSON bodies (an nginx 413 page) carry no message worth showing.
    return data.trim().startsWith('<') ? null : unwrapJsonString(data)
  }
  if (!data || typeof data !== 'object') return null
  if (typeof data.message === 'string' && data.message.trim()) return data.message.trim()
  const detail = fromDetail(data.detail)
  return detail && detail.trim() ? detail.trim() : null
}
